import { isTauriMobile } from '@/services/platformService'

const PERMISSION_DENIED = 1
const POSITION_UNAVAILABLE = 2
const TIMEOUT = 3

export interface GeoCoordinates {
  latitude: number
  longitude: number
  accuracy: number
  altitude?: number | null
  altitudeAccuracy?: number | null
  heading?: number | null
  speed?: number | null
}

export interface GeoPosition {
  coords: GeoCoordinates
  timestamp: number
}

export interface GeoError {
  code: number
  message: string
  PERMISSION_DENIED: number
  POSITION_UNAVAILABLE: number
  TIMEOUT: number
}

export type GeoWatchHandle = {
  id: number
  type: 'web' | 'tauri'
}

export type GeoPositionOptions = PositionOptions
export type GeoPermissionState = PermissionState | 'prompt-with-rationale' | 'unknown'

const createUnavailableError = (message?: string): GeoError => ({
  code: POSITION_UNAVAILABLE,
  message: message ?? 'Geolocation is not supported',
  PERMISSION_DENIED,
  POSITION_UNAVAILABLE,
  TIMEOUT,
})

const createPermissionDeniedError = (message?: string): GeoError => ({
  code: PERMISSION_DENIED,
  message: message ?? 'Location permission denied',
  PERMISSION_DENIED,
  POSITION_UNAVAILABLE,
  TIMEOUT,
})

const normalizeWebPosition = (position: GeolocationPosition): GeoPosition => ({
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    altitude: position.coords.altitude,
    altitudeAccuracy: position.coords.altitudeAccuracy,
    heading: position.coords.heading,
    speed: position.coords.speed,
  },
  timestamp: position.timestamp,
})

const normalizeWebError = (error: GeolocationPositionError): GeoError => ({
  code: error.code,
  message: error.message,
  PERMISSION_DENIED,
  POSITION_UNAVAILABLE,
  TIMEOUT,
})

type TauriGeolocationModule = typeof import('@tauri-apps/plugin-geolocation')
type TauriPosition = Awaited<ReturnType<TauriGeolocationModule['getCurrentPosition']>>

const normalizeTauriPosition = (position: TauriPosition): GeoPosition => ({
  coords: {
    latitude: position.coords.latitude,
    longitude: position.coords.longitude,
    accuracy: position.coords.accuracy,
    altitude: position.coords.altitude,
    altitudeAccuracy: position.coords.altitudeAccuracy,
    heading: position.coords.heading,
    speed: position.coords.speed,
  },
  timestamp: position.timestamp,
})

const normalizeTauriOptions = (options?: GeoPositionOptions) => ({
  enableHighAccuracy: options?.enableHighAccuracy ?? false,
  timeout: options?.timeout ?? 15000,
  maximumAge: options?.maximumAge ?? 0,
})

const createTauriError = (error: unknown): GeoError => {
  const message = error instanceof Error ? error.message : String(error)
  const normalizedMessage = message.toLowerCase()
  if (normalizedMessage.includes('denied') || normalizedMessage.includes('permission')) {
    return createPermissionDeniedError(message)
  }
  if (normalizedMessage.includes('timeout')) {
    return {
      code: TIMEOUT,
      message,
      PERMISSION_DENIED,
      POSITION_UNAVAILABLE,
      TIMEOUT,
    }
  }
  return createUnavailableError(message)
}

const getTauriGeolocation = async () => import('@tauri-apps/plugin-geolocation')

const ensureTauriLocationPermission = async (
  geolocation: TauriGeolocationModule,
): Promise<void> => {
  let permissions = await geolocation.checkPermissions()
  if (permissions.location === 'prompt' || permissions.location === 'prompt-with-rationale') {
    permissions = await geolocation.requestPermissions(['location'])
  }

  if (permissions.location !== 'granted') {
    throw createPermissionDeniedError('Location permission denied')
  }
}

export const ensureGeolocationPermission = async (): Promise<void> => {
  if (!isTauriMobile()) return

  try {
    await ensureTauriLocationPermission(await getTauriGeolocation())
  } catch (error) {
    if (typeof error === 'object' && error !== null && 'code' in error) {
      throw error
    }
    throw createTauriError(error)
  }
}

export const getCurrentPosition = async (options?: GeoPositionOptions): Promise<GeoPosition> => {
  if (isTauriMobile()) {
    try {
      const geolocation = await getTauriGeolocation()
      await ensureTauriLocationPermission(geolocation)
      return normalizeTauriPosition(
        await geolocation.getCurrentPosition(normalizeTauriOptions(options)),
      )
    } catch (error) {
      if (typeof error === 'object' && error !== null && 'code' in error) {
        throw error
      }
      throw createTauriError(error)
    }
  }

  if (typeof window === 'undefined' || !navigator.geolocation) {
    throw createUnavailableError()
  }

  return new Promise<GeoPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(normalizeWebPosition(position)),
      (error) => reject(normalizeWebError(error)),
      options,
    )
  })
}

export const watchPosition = async (
  options: GeoPositionOptions,
  onPosition: (position: GeoPosition) => void,
  onError?: (error: GeoError) => void,
): Promise<GeoWatchHandle> => {
  if (isTauriMobile()) {
    try {
      const geolocation = await getTauriGeolocation()
      await ensureTauriLocationPermission(geolocation)
      const id = await geolocation.watchPosition(
        normalizeTauriOptions(options),
        (position, error) => {
          if (error) {
            onError?.(createTauriError(error))
            return
          }
          if (!position) {
            onError?.(createUnavailableError('No location update received'))
            return
          }
          onPosition(normalizeTauriPosition(position))
        },
      )
      return { id, type: 'tauri' }
    } catch (error) {
      const geoError =
        typeof error === 'object' && error !== null && 'code' in error
          ? (error as GeoError)
          : createTauriError(error)
      onError?.(geoError)
      throw geoError
    }
  }

  if (typeof window === 'undefined' || !navigator.geolocation) {
    const error = createUnavailableError()
    onError?.(error)
    throw error
  }

  const id = navigator.geolocation.watchPosition(
    (position) => onPosition(normalizeWebPosition(position)),
    (error) => onError?.(normalizeWebError(error)),
    options,
  )

  return { id, type: 'web' }
}

export const clearWatch = async (handle: GeoWatchHandle | null): Promise<void> => {
  if (!handle) return

  if (handle.type === 'tauri') {
    const geolocation = await getTauriGeolocation()
    await geolocation.clearWatch(handle.id)
    return
  }

  if (typeof window === 'undefined' || !navigator.geolocation) return

  navigator.geolocation.clearWatch(handle.id)
}

export const getGeolocationPermissionState = async (): Promise<GeoPermissionState> => {
  if (isTauriMobile()) {
    try {
      const geolocation = await getTauriGeolocation()
      return (await geolocation.checkPermissions()).location
    } catch {
      return 'unknown'
    }
  }

  if (typeof window === 'undefined' || !navigator.geolocation) {
    return 'denied'
  }

  if (!navigator.permissions?.query) {
    return 'unknown'
  }

  try {
    return (await navigator.permissions.query({ name: 'geolocation' })).state
  } catch {
    return 'unknown'
  }
}

export const isGeolocationSupported = (): boolean => {
  return isTauriMobile() || (typeof window !== 'undefined' && !!navigator.geolocation)
}
